import {
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0x8020
const RADIO_BLOCKS = 0x80
const BLOCK_SIZE = 0x100
const SCAN_START = 0x8000
const INDEX_START = 0x0300
const CHANNEL_START = 0x2000
const CHANNEL_SIZE = 48
const CHANNEL_COUNT = 250

type TKx140Spec = {
  id: string
  model: string
  summary: string
  modelBytes: readonly number[]
}

export type TKx140Profile = {
  protocol: 'kenwood-tkx140'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  stopBits: 2
  channelMinimum: 1
  channelMaximum: number
  nameLength: 10
  memorySize: number
  modelBytes: readonly number[]
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly TKx140Spec[] = [
  {
    id: 'kenwood-tk-2140k',
    model: 'TK-2140K',
    summary: 'Kenwood TK-2140K VHF portable with zone-indexed memories.',
    modelBytes: [0x50, 0x32, 0x31, 0x34, 0x30, 0x04, 0xff, 0xf1],
  },
  {
    id: 'kenwood-tk-3140k',
    model: 'TK-3140K',
    summary: 'Kenwood TK-3140K UHF portable with zone-indexed memories.',
    modelBytes: [0x50, 0x33, 0x31, 0x34, 0x30, 0x06, 0xff, 0xf1],
  },
  {
    id: 'kenwood-tk-3140k2',
    model: 'TK-3140K2',
    summary: 'Kenwood TK-3140K2 400-470 MHz portable.',
    modelBytes: [0x50, 0x33, 0x31, 0x34, 0x30, 0x07, 0xff, 0xf1],
  },
  {
    id: 'kenwood-tk-3140k3',
    model: 'TK-3140K3',
    summary: 'Kenwood TK-3140K3 450-520 MHz portable.',
    modelBytes: [0x50, 0x33, 0x31, 0x34, 0x30, 0x08, 0xff, 0xf1],
  },
]

export const TKX140_PROFILES: readonly TKx140Profile[] = SPECS.map((spec) => ({
  protocol: 'kenwood-tkx140',
  id: spec.id,
  vendor: 'Kenwood',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '250 zones-mapped channels', '10-character names'],
  baudRate: 9600,
  stopBits: 2,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 10,
  memorySize: MEMORY_SIZE,
  modelBytes: spec.modelBytes,
  powerLevels: ['Low', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function readUint16LE(raw: Uint8Array, offset: number): number {
  return raw[offset] | (raw[offset + 1] << 8)
}

function writeUint16LE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = value & 0xff
  raw[offset + 1] = (value >> 8) & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x2000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x01ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value = (Number.parseInt(channel.dtcs.toString(), 8) & 0x01ff) | 0x2800
    if (channel.dtcsPolarity === 'R') value |= 0x8000
    return value
  }
  return 0xffff
}

function indexEntry(
  image: Uint8Array,
  index: number,
): { zone: number; memory: number; slot: number } {
  const offset = INDEX_START + index * 4
  return {
    zone: image[offset],
    memory: image[offset + 1],
    slot: image[offset + 3],
  }
}

function slotForNumber(image: Uint8Array, number: number): number | null {
  const entry = indexEntry(image, number - 1)
  if (entry.zone === 0xff || entry.memory === 0xff || entry.slot === 0xff) {
    return null
  }
  return entry.slot
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

export function parseTKx140Channel(
  image: Uint8Array,
  profile: TKx140Profile,
  number: number,
): WebChannel {
  const slot = slotForNumber(image, number)
  if (slot === null) return emptyChannel(number)
  const start = CHANNEL_START + slot * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(12, 16))
  if (!rxFrequency) return emptyChannel(number)
  const txAll = raw.slice(16, 20).every((value) => value === 0xff)
  const txFrequency = txAll ? null : decodeLbcd(raw.slice(16, 20)) ?? rxFrequency
  const txTone = decodeTone(readUint16LE(raw, 24))
  const rxTone = decodeTone(readUint16LE(raw, 22))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  const scan = (image[SCAN_START + Math.floor(slot / 8)] & (1 << (slot % 8))) !== 0
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(2, 12)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[32] & 0x01 ? 'FM' : 'NFM',
    power: raw[32] & 0x04 ? 'High' : 'Low',
    skip: !scan,
  }
}

export function parseTKx140Channels(
  image: Uint8Array,
  profile: TKx140Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseTKx140Channel(image, profile, index + 1),
  )
}

export function applyTKx140Channel(
  image: Uint8Array,
  profile: TKx140Profile,
  channel: WebChannel,
): void {
  let slot = slotForNumber(image, channel.number)
  if (slot === null) {
    if (channel.empty) return
    for (let index = 0; index < CHANNEL_COUNT; index += 1) {
      const entry = indexEntry(image, index)
      if (entry.zone === 0xff) {
        let available = -1
        for (let candidate = 0; candidate < CHANNEL_COUNT; candidate += 1) {
          if (image[CHANNEL_START + candidate * CHANNEL_SIZE] === 0xff) {
            available = candidate
            break
          }
        }
        if (available < 0) throw new Error('The radio has no free channel slots.')
        const offset = INDEX_START + index * 4
        image.set([1, channel.number, 0, available], offset)
        slot = available
        break
      }
    }
    if (slot === null) throw new Error('The radio has no free index entries.')
  }
  const start = CHANNEL_START + slot * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  const scanOffset = SCAN_START + Math.floor(slot / 8)
  const scanMask = 1 << (slot % 8)
  image[scanOffset] = channel.skip
    ? image[scanOffset] & ~scanMask
    : image[scanOffset] | scanMask
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) raw.fill(0xff)
  raw[0] = channel.number
  raw[1] = raw[1] === 0xff ? 1 : raw[1]
  raw[2] = 0x20
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 10)
    .padEnd(10, ' ')
  for (let index = 0; index < 10; index += 1) {
    raw[2 + index] = name.charCodeAt(index)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(12, 16))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(16, 20))
  } else {
    raw.subarray(16, 20).fill(0xff)
  }
  raw[20] = (raw[20] & 0xf0) | 0x03
  raw[21] = (raw[21] & 0xf0) | 0x03
  const tone = encodeTone(channel)
  writeUint16LE(raw, 22, tone)
  writeUint16LE(raw, 24, tone)
  raw[32] =
    (raw[32] & 0xf8) |
    (channel.mode === 'FM' ? 0x01 : 0) |
    (channel.power === 'High' ? 0x04 : 0)
}

async function readAck(transport: RadioTransport): Promise<void> {
  const response = (await transport.readExact(1, 2500))[0]
  if (response !== ACK) {
    throw new Error(
      `The Kenwood radio rejected the operation with 0x${response.toString(16)}.`,
    )
  }
}

async function enter(
  profile: TKx140Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  await readAck(transport)
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 2500)
  if (!identity.every((value, index) => value === profile.modelBytes[index])) {
    throw new Error(`The connected radio does not identify as ${profile.model}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
  await transport.write(new TextEncoder().encode('P'))
  await transport.readExact(10, 2500)
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
}

export async function readTKx140RadioWithTransport(
  profile: TKx140Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    for (let block = 0; block < RADIO_BLOCKS; block += 1) {
      await transport.write(Uint8Array.of(0x52, 0, block))
      const command = (await transport.readExact(1, 3000))[0]
      let data: Uint8Array
      if (command === 0x5a) {
        const fill = (await transport.readExact(1, 2000))[0]
        data = new Uint8Array(BLOCK_SIZE).fill(fill)
      } else if (command === 0x57) {
        data = await transport.readExact(BLOCK_SIZE, 4000)
        const expected = data.reduce((sum, value) => (sum + value) & 0xff, 0)
        if ((await transport.readExact(1, 2000))[0] !== expected) {
          throw new Error(`TK-x140 checksum failed at block ${block}.`)
        }
      } else {
        throw new Error(`Unexpected TK-x140 command 0x${command.toString(16)}.`)
      }
      image.set(data, block * BLOCK_SIZE)
      await transport.write(Uint8Array.of(ACK))
      try {
        await readAck(transport)
      } catch (error) {
        throw new Error(
          `TK-x140 block ${block}: ${error instanceof Error ? error.message : String(error)}`,
        )
      }
      onProgress?.({
        current: block + 1,
        total: RADIO_BLOCKS + 2,
        label: 'Reading Kenwood TK-x140 memory',
      })
    }
    for (let index = 0; index < 2; index += 1) {
      const address = 0x0320 + index * 0x10
      await transport.write(Uint8Array.of(0x53, (address >> 8) & 0xff, address & 0xff, 0x10))
      if ((await transport.readExact(1, 2500))[0] !== 0x58) {
        throw new Error(`Unexpected TK-x140 scan response at 0x${address.toString(16)}.`)
      }
      image.set(await transport.readExact(0x10, 2500), SCAN_START + index * 0x10)
      await transport.write(Uint8Array.of(ACK))
      await readAck(transport)
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeTKx140RadioWithTransport(
  profile: TKx140Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than TK-x140 memory.')
  }
  await enter(profile, transport)
  try {
    for (let block = 0; block < RADIO_BLOCKS; block += 1) {
      if (block >= 0x54 && block < 0x60) continue
      const start = block * BLOCK_SIZE
      const data = image.slice(start, start + BLOCK_SIZE)
      if (data.every((value) => value === data[0])) {
        await transport.write(Uint8Array.of(0x5a, 0, block, data[0]))
      } else {
        const packet = new Uint8Array(4 + BLOCK_SIZE)
        packet.set([0x57, 0, block])
        packet.set(data, 3)
        const checksum = data.reduce((sum, value) => (sum + value) & 0xff, 0)
        packet[3 + BLOCK_SIZE] = checksum
        await transport.write(packet)
      }
      try {
        await readAck(transport)
      } catch (error) {
        throw new Error(
          `TK-x140 write block ${block}: ${error instanceof Error ? error.message : String(error)}`,
        )
      }
      onProgress?.({
        current: block + 1,
        total: RADIO_BLOCKS,
        label: 'Writing Kenwood TK-x140 memory',
      })
    }
    for (let index = 0; index < 2; index += 1) {
      const radioAddress = 0x0320 + index * 0x10
      const memoryAddress = SCAN_START + index * 0x10
      const data = image.slice(memoryAddress, memoryAddress + 0x10)
      const packet = new Uint8Array(21)
      packet.set([
        0x58,
        (radioAddress >> 8) & 0xff,
        radioAddress & 0xff,
        0x10,
      ])
      packet.set(data, 4)
      packet[20] = data.reduce((sum, value) => (sum + value) % 255, 0)
      await transport.write(packet)
      await readAck(transport)
      await transport.write(Uint8Array.of(ACK))
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
